import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Form, FormType } from '../interfaces/form.interface';
import type { ReactiveFormPayloadSerializerResolvers } from '../interfaces/reactive-form-serializer.interface';
import { ReactiveFormSubmitFacade } from '../utils/facade/reactive-form-submit.facade';
import {
    REACTIVE_FORM_BUILDER_STORAGE,
    REACTIVE_FORM_NOOP_REQUEST_HANDLER,
    type ReactiveFormRequestHandler,
} from '../adapters/reactive-form-builder.adapters';

@Component({
    selector: 'app-reactive-request-button',
    standalone: false,
    templateUrl: './request-button.component.html',
    styleUrls: ['./request-button.component.scss']
})
export class ReactiveRequestButtonComponent<T> implements OnInit {
    @Input() form!: Form<T>;
    @Input() model!: FormGroup;
    @Input() recordHasAssociatedData = false;
    @Input() request: ReactiveFormRequestHandler<any> = REACTIVE_FORM_NOOP_REQUEST_HANDLER;
    @Input() getRequest: ReactiveFormRequestHandler<any> = REACTIVE_FORM_NOOP_REQUEST_HANDLER;
    @Input() lockOnSuccess = false;
    @Input() resolveDropdownRawValue: ((fieldName: string, value: unknown) => unknown | null) | null = null;
    @Input() resolveDropdownBackendValue: ((fieldName: string, value: unknown) => unknown | null) | null = null;

    @Output() goBack: EventEmitter<T> = new EventEmitter<T>();
    @Output() afterSend: EventEmitter<T> = new EventEmitter<T>();

    public readonly REQUEST_BUTTON_TYPES = FormType;

    private readonly storage = inject(REACTIVE_FORM_BUILDER_STORAGE);

    private readonly submitFacade: ReactiveFormSubmitFacade<T>;

    constructor() {
        this.submitFacade = new ReactiveFormSubmitFacade<T>({
            getForm: () => this.form ?? null,
            getModel: () => this.model ?? null,
            getRecordHasAssociatedData: () => this.recordHasAssociatedData,
            getLockOnSuccess: () => this.lockOnSuccess,
            getRequest: () => this.request ?? null,
            getGetRequest: () => this.getRequest ?? null,
            isDemoUser: () => this.storage.getUser()?.rol === 'demo',
            emitGoBack: () => this.goBack.emit(),
            emitAfterSend: (response) => this.afterSend.emit(response),
            getSerializerResolvers: () => this.getSerializerResolvers(),
        });
    }

    public get validationError(): string | null {
        return this.submitFacade.validationError;
    }

    public get validationSummary(): string | null {
        return this.submitFacade.validationSummary;
    }

    public get warning(): string | null {
        return this.submitFacade.warning;
    }

    public get requestError(): readonly string[] {
        return this.submitFacade.requestError;
    }

    /**
     * Returns the user-facing label according to the real submit state.
     */
    public get buttonLabel(): string {
        return this.submitFacade.buttonLabel;
    }

    /**
     * Returns CSS classes for the action button.
     */
    public get buttonClasses(): Record<string, boolean> {
        return this.submitFacade.buttonClasses;
    }

    /**
     * Returns CSS classes for the icon rendered inside the button.
     */
    public get iconClasses(): Record<string, boolean> {
        return this.submitFacade.iconClasses;
    }

    /**
     * Indicates whether the button must be disabled.
     */
    public get isButtonDisabled(): boolean {
        return this.submitFacade.isButtonDisabled;
    }

    /**
     * Indicates whether the secondary text for creating or duplicating another record must be shown.
     */
    public get showRepeatActionLabel(): boolean {
        return this.submitFacade.showRepeatActionLabel;
    }

    /**
     * Returns the secondary label for repeated creation or duplication.
     */
    public get repeatActionLabel(): string {
        return this.submitFacade.repeatActionLabel;
    }

    /**
     * Sets generic errors for requests.
     */
    public ngOnInit(): void {
        this.submitFacade.initialize();
    }

    /**
     * Evaluates the form and sends the request when the payload is valid.
     */
    public submit(): void {
        this.submitFacade.submit();
    }

    /**
     * Supplies dropdown resolvers to the serializer so primitive control values can be resolved through the option store.
     */
    private getSerializerResolvers(): ReactiveFormPayloadSerializerResolvers {
        const rawValueResolver = this.resolveDropdownRawValue;
        const backendValueResolver = this.resolveDropdownBackendValue;

        if (rawValueResolver === null || rawValueResolver === undefined) {
            if (backendValueResolver === null || backendValueResolver === undefined) {
                return {};
            }

            return { resolveDropdownBackendValue: backendValueResolver };
        }

        if (backendValueResolver === null || backendValueResolver === undefined) {
            return { resolveDropdownRawValue: rawValueResolver };
        }

        return {
            resolveDropdownRawValue: rawValueResolver,
            resolveDropdownBackendValue: backendValueResolver,
        };
    }
}
